import * as moment from 'moment';

import { Action, Selector, State } from '@ngxs/store';
import {
  DialogQuitterPageComponent,
  ResultatQuitter
} from '../../shared/components/dialog-quitter-page/dialog-quitter-page.component';
import { HideLoader, ShowLoader } from '../../shared/personne.state';
import { cloneDeep, get, merge } from 'lodash';

import { AppConfigService } from '../../app-config.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OpenModalService } from '../../shared/services/open-modal/open-modal.service';
import { PreautorisationEnLigneApiService } from '../experimente/services/preautorisation-en-ligne-api.service';
import { PreautorisationFlowHelper } from '../experimente/helpers/preautorisation-flow-helper/preautorisation-flow-helper';
import { PreautorisationModel } from '../experimente/models/preautorisation.model';
import { Router } from '@angular/router';
import { StatusPreautorisation } from '../../shared/modeles/variables';
import { TypeProjet } from '../../shared/constants/type-projet.enum';

const defaultState = {
  currentStep: 'page_accueil',
  previousStep: null,
  coemprunteur: null,
  typePropriete: null,
  residencePrincipale: null,
  salarie: null,
  domaine: null,
  metier: null,
  preautorisation: {},
  preautorisationAvantModif: {},
  isCapaciteCompleted: false,
  modifyingStep: false,
  isPreautLoaded: false,
  isConsentTextAccepted: false,
  numeroDemande: ''
};

export interface PreautorisationEnLigneStateModel {
  currentStep: string;
  previousStep: string;
  coemprunteur: string;
  typePropriete: string;
  residencePrincipale: string;
  salarie: string;
  domaine: string;
  metier: string;
  modifyingStep: boolean;
  preautorisation: PreautorisationModel;
  preautorisationAvantModif: PreautorisationModel;
  isCapaciteCompleted: boolean;
  isPreautLoaded: boolean;
  validationError?: any;
  isConsentTextAccepted: boolean;
  numeroDemande: string;
}

export class LoadPreautorisationEnLigne {
  static type = '[Preautorisation en ligne] LoadPreautorisationEnLigne';
}

// Attention c'est le call qui initialise l'objet preautorisation (Anciennement dans createPreauth du spa-bff)
export class InitPreautorisationEnLigne {
  static type = '[Preautorisation en ligne] InitPreautorisationEnLigne';

  constructor() {}
}

export class SetPreautorisationEnLigne {
  static type = '[Preautorisation en ligne] SetPreautorisationEnLigne';

  constructor(public preautorisation: PreautorisationModel) {}
}

export class ResetPreautorisationEnLigne {
  static type = '[Preautorisation en ligne] ResetPreautorisationEnLigne';
}

export class UpdatePreautorisation {
  static type = '[Preautorisation en ligne] UpdatePreautorisation';
  constructor(public partialPreautorisation: Partial<PreautorisationModel>) {}
}

export class GoToStep {
  static type = '[Preautorisation en ligne] GoToStep';

  constructor(public step: string) {}
}

export class SubmitPreautorisationEnLigne {
  static type = '[Preautorisation en ligne] SubmitPreautorisationEnLigne';

  constructor(public consentId: string) {}
}

export class EditPreautorisationStep {
  static type = '[Preautorisation en ligne] EditPreautorisationStep';

  constructor(public step: string) {}
}

export class AnnulerPreautorisationEnLigne {
  static type = '[Preautorisation en ligne] AnnulerPreautorisationEnLigne';
}

export class SetStatusPreautorisationEnLigne {
  static type = '[Preautorisation en ligne] SetStatusPreautorisationEnLigne';

  constructor(public status: StatusPreautorisation) {}
}

export class PatchPreautorisationDebugOnly {
  static type = '[Preautorisation en ligne] PatchPreautorisationDebugOnly';

  constructor(public preautorisation: Partial<PreautorisationModel>) {}
}

export class ValidateCodeInvitation {
  static type = '[Preautorisation en ligne] ValidateCodeInvitation';
  constructor(public codeInvitation: string) {}
}

export class ValidatePreadmissibilite {
  static type = '[Preautorisation en ligne] ValidatePreadmissibilite';
}

export class HandleHTTPError {
  static type = '[Preautorisation en ligne] HandleHTTPError';

  constructor(public httpStatus: number) {}
}

export class AnnulerPreautorisationAvecConfirmation {
  static type = '[Preautorisation en ligne] AnnulerPreautorisationAvecConfirmation';
  constructor(public returnUrl: string) {}
}

@Injectable()
@State<PreautorisationModel>({
  name: 'preautorisationEnLigne'
})
export class PreautorisationEnLigneState {
  constructor(
    private readonly _preautorisationApiService: PreautorisationEnLigneApiService,
    private readonly _appConfigService: AppConfigService,
    private readonly _router: Router,
    private readonly _openModalService: OpenModalService
  ) {}

  static hasFieldCompleted(champs) {
    for (const champ of champs) {
      if (typeof champ === undefined || champ == null || Number.isNaN(champ) || champ === '') {
        return false;
      }
    }
    return true;
  }

  static isPreautorisationEnLigneCompleted(preautorisation: PreautorisationModel) {
    const keys = Object.keys(preautorisation);
    const currentPreautorisationFields = keys.map(e => {
      return preautorisation[e];
    });

    return PreautorisationEnLigneState.hasFieldCompleted(currentPreautorisationFields);
  }

  @Selector()
  static isCompleted(state: PreautorisationEnLigneStateModel) {
    return PreautorisationEnLigneState.isPreautorisationEnLigneCompleted(state.preautorisation);
  }

  @Selector()
  static getPreautorisation(state: PreautorisationEnLigneStateModel): PreautorisationModel {
    return get(state, 'preautorisation', null);
  }

  @Selector()
  static getStatusPreautorisation(state: PreautorisationEnLigneStateModel): StatusPreautorisation {
    return get(state, 'preautorisation.statut', null) as StatusPreautorisation;
  }

  @Selector()
  static getStatusAndIsLoadedPreautorisation(
    state: PreautorisationEnLigneStateModel
  ): {
    isPreautLoaded: boolean;
    status: StatusPreautorisation;
    currentStep: string;
    isSecondCoemprunteur: boolean;
    isCoemprunteur: boolean;
  } {
    return {
      isPreautLoaded: state.isPreautLoaded,
      status: get(state, 'preautorisation.statut', null),
      currentStep: get(state, 'currentStep', null),
      isSecondCoemprunteur: PreautorisationEnLigneState.isSecondCoemprunteur(state),
      isCoemprunteur: PreautorisationEnLigneState.isCoemprunteur(state)
    };
  }

  @Selector()
  static getMontantPreautorisation(state: PreautorisationEnLigneStateModel): number {
    const montantPropriete: number = PreautorisationEnLigneState.getMontantPropriete(state);
    const miseDeFonds: number = get(state, 'preautorisation.miseDeFonds', 0);

    return Math.max(montantPropriete - miseDeFonds, 0);
  }

  @Selector()
  static getMontantPropriete(state: PreautorisationEnLigneStateModel): number {
    const statut: StatusPreautorisation = get(state, 'preautorisation.statut', null);
    const montantContreOffre: number = get(state, 'preautorisation.resultat.montantContreOffre', 0);
    const montantPropriete: number = get(state, 'preautorisation.montantPropriete', 0);

    return statut === StatusPreautorisation.ContreOffreAcceptee ? montantContreOffre : montantPropriete;
  }

  @Selector()
  static getIsPreautExpiringSoon(state: PreautorisationEnLigneStateModel): boolean {
    const lastDayBeforeExpiration = PreautorisationEnLigneState.getLastDayBeforeExpiration(state);
    if (!lastDayBeforeExpiration) {
      return false;
    }
    const today = new Date();
    const expirationNotificationStartDate = moment(lastDayBeforeExpiration)
      .utc()
      .subtract(30, 'days')
      .toDate();
    return today >= expirationNotificationStartDate && today <= lastDayBeforeExpiration;
  }

  @Selector()
  static getLastDayBeforeExpiration(state: PreautorisationEnLigneStateModel): Date {
    const dateExpiration: string = get(state, 'preautorisation.resultat.dateExpiration', null);
    if (!dateExpiration) {
      return null;
    }
    return moment(dateExpiration)
      .utc()
      .toDate();
  }

  @Selector()
  static getDateExpirationSelectionTaux(state: PreautorisationEnLigneStateModel): Date {
    const dateExpirationGarantieTaux = get(state, 'preautorisation.dateExpirationSelectionTaux', null);

    return dateExpirationGarantieTaux
      ? moment(dateExpirationGarantieTaux)
          .utc()
          .toDate()
      : null;
  }

  @Selector()
  static isCoemprunteur(state: PreautorisationEnLigneStateModel): boolean {
    return PreautorisationFlowHelper.isCoemprunteur(state.preautorisation);
  }

  @Selector()
  static isFirstCoemprunteur(state: PreautorisationEnLigneStateModel): boolean {
    return PreautorisationFlowHelper.isFirstCoemprunteur(state.preautorisation);
  }

  @Selector()
  static isSecondCoemprunteur(state: PreautorisationEnLigneStateModel): boolean {
    return PreautorisationFlowHelper.isSecondCoemprunteur(state.preautorisation);
  }

  @Selector()
  static donneesContreOffre(state: PreautorisationEnLigneStateModel) {
    const dateExpirationContreOffre = moment(
      get(state.preautorisation, 'resultat.dateExpirationContreOffre', null)
    ).utc();
    const miseDeFonds = get(state.preautorisation, 'miseDeFonds');

    return {
      dateExpirationContreOffre: dateExpirationContreOffre.toDate(),
      dateContreOffreAnnulee: dateExpirationContreOffre.add(1, 'day').toDate(),
      montantApprouve: get(state.preautorisation, 'resultat.montantContreOffre', null) - miseDeFonds,
      montantDemande: get(state.preautorisation, 'montantPropriete') - miseDeFonds,
      canTakeAction: !PreautorisationFlowHelper.isFirstCoemprunteur(state.preautorisation)
    };
  }

  @Action(LoadPreautorisationEnLigne)
  async loadPreautorisationEnLigne({ patchState, dispatch }) {
    await this._preautorisationApiService
      .getPreautorisation()
      .toPromise()
      .then((preautorisation: PreautorisationModel) => {
        patchState({ preautorisation: preautorisation, isPreautLoaded: true });
        this._appConfigService.configData.preautorisationEnLigne = preautorisation;
      })
      .catch((error: HttpErrorResponse) => {
        if (error.status === 404) {
          // Server returns 404 if there is no preaut.
          dispatch(new ResetPreautorisationEnLigne());
        }
      });
  }

  @Action(InitPreautorisationEnLigne)
  async initPreautorisationEnLigne({ dispatch, getState, patchState }) {
    dispatch(new ShowLoader());
    await this._preautorisationApiService
      .initPreautorisation()
      .toPromise()
      .then((preautorisation: PreautorisationModel) => {
        if (preautorisation) {
          preautorisation.type = TypeProjet.PremierAcheteur;
          // Ces 2 valeurs sont gérées par le frontend
          delete preautorisation.indCoemprunteur;
          delete preautorisation.indCoemprunteurPrincipal;
        }
        patchState({ preautorisation: { ...getState().preautorisation, ...preautorisation }, isPreautLoaded: true });
      })
      .catch((error: HttpErrorResponse) => {
        if (error.status === 404) {
          // Server returns 404 if there is no preaut.
          patchState({ ...defaultState, isPreautLoaded: true });
        } else if (error.status === 409) {
          this._router.navigateByUrl('preautorisation-simplifiee/en-cours', {
            state: { typeProjet: error.error.type || TypeProjet.PremierAcheteur, statut: error.error.statut }
          });
        }
      })
      .finally(() => {
        dispatch(new HideLoader());
      });
  }

  @Action(SetPreautorisationEnLigne)
  async setPreautorisation({ getState, patchState }, { preautorisation }: SetPreautorisationEnLigne) {
    const state = getState();
    const updatedPreautorisation: PreautorisationModel = merge(state.preautorisation, preautorisation);

    patchState({
      preautorisation: updatedPreautorisation,
      isPreautLoaded: true
    });
  }

  @Action(ResetPreautorisationEnLigne)
  resetPreautorisation({ patchState }) {
    patchState({ ...defaultState, preautorisation: {}, isPreautLoaded: true });
  }

  @Action(GoToStep)
  goToStep({ patchState, getState }, { step }: GoToStep) {
    const state = getState();

    if (step === state.currentStep) {
      return;
    }

    const newState: Partial<PreautorisationEnLigneStateModel> = {
      currentStep: step
    };
    if (state.currentStep !== 'non_eligible') {
      newState.previousStep = state.currentStep;
    }
    patchState(newState);
  }

  @Action(UpdatePreautorisation)
  async updatePreautorisation({ getState, patchState, dispatch }, { partialPreautorisation }: UpdatePreautorisation) {
    const state = getState();
    const updatedPreautorisation: PreautorisationModel = merge(state.preautorisation, partialPreautorisation);

    patchState({
      preautorisation: updatedPreautorisation
    });
  }

  @Action(SubmitPreautorisationEnLigne)
  async submitPreautorisationEnLigne({ getState, patchState }, { consentId }: SubmitPreautorisationEnLigne) {
    const state = getState();

    const preautorisation = await this._preautorisationApiService
      .submitPreautorisation(state.preautorisation, consentId, state.numeroDemande)
      .toPromise();

    if (preautorisation.codeErreur) {
      patchState({
        preautorisation: defaultState.preautorisation,
        isPreautLoaded: true,
        validationError: {
          codeErreur: preautorisation.codeErreur,
          prenomCoemprunteur: preautorisation.prenomCoemprunteur
        }
      });
    } else {
      patchState({
        preautorisation
      });
    }
  }

  @Action(EditPreautorisationStep)
  editPreautorisationStep({ patchState, getState }, { step }: EditPreautorisationStep) {
    if (!getState().modifyingStep) {
      patchState({
        preautorisationAvantModif: cloneDeep(getState().preautorisation)
      });
    }

    patchState({
      currentStep: step,
      modifyingStep: true
    });
  }
  @Action(AnnulerPreautorisationEnLigne)
  async annulerPreautorisationEnLigne({ getState, dispatch, patchState }) {
    const state = getState();
    if (get(state, 'preautorisation.id')) {
      await this._preautorisationApiService.deletePreautorisation(state.preautorisation.id).toPromise();
      await dispatch(new LoadPreautorisationEnLigne()).toPromise();
    } else {
      patchState({ ...defaultState, isPreautLoaded: state.isPreautLoaded, preautorisation: {} });
    }
  }

  @Action(SetStatusPreautorisationEnLigne)
  async setStatus({ patchState, getState }, { status }: SetStatusPreautorisationEnLigne) {
    const state = getState();
    patchState({ preautorisation: merge(state.preautorisation, { statut: status }) });
  }

  @Action(PatchPreautorisationDebugOnly)
  patchPreautorisationDebugOnly({ patchState, getState }, { preautorisation }: PatchPreautorisationDebugOnly) {
    const state = getState();
    patchState({ preautorisation: merge(state.preautorisation, preautorisation) });
  }
}
